import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

const RateApprovalModal = ({ isOpen, onClose, request, onApprovalComplete }) => {
  const [action, setAction] = useState(null); // 'approve' or 'reject'
  const [approvedRates, setApprovedRates] = useState({
    check_rate: request.requested_check_rate,
    weekday_cash_rate: request.requested_weekday_cash_rate,
    weekend_cash_rate: request.requested_weekend_cash_rate
  });
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(request.requested_effective_date || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [useCustomRates, setUseCustomRates] = useState(false);
  
  // Comparison data
  const [comparisonData, setComparisonData] = useState([]);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [employeeInfo, setEmployeeInfo] = useState(null);
  const [lastRateAdjustmentDate, setLastRateAdjustmentDate] = useState(null);
  const [payrollName, setPayrollName] = useState(null);

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const downloadRequestPDF = async () => {
    // Build compact rates HTML - single row layout
    let ratesHTML = '<div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-bottom: 10px;">';
    
    if (request.current_check_rate > 0 || request.requested_check_rate > 0) {
      ratesHTML += `
        <div style="background: #EFF6FF; padding: 8px; border-left: 3px solid #2563EB;">
          <div style="font-weight: bold; font-size: 10px; margin-bottom: 4px;">Check Rate / 支票工资</div>
          <div style="font-size: 10px; margin-bottom: 2px;">
            <span>Current:</span> <strong>$${parseFloat(request.current_check_rate || 0).toFixed(2)}</strong>
          </div>
          <div style="font-size: 10px; margin-bottom: 2px;">
            <span>Requested:</span> <strong style="color: #2563EB;">$${parseFloat(request.requested_check_rate || 0).toFixed(2)}</strong>
          </div>
        </div>
      `;
    }
    
    if (request.current_weekday_cash_rate > 0 || request.requested_weekday_cash_rate > 0) {
      ratesHTML += `
        <div style="background: #F0FDF4; padding: 8px; border-left: 3px solid #16A34A;">
          <div style="font-weight: bold; font-size: 10px; margin-bottom: 4px;">Weekday Cash / 工作日现金</div>
          <div style="font-size: 10px; margin-bottom: 2px;">
            <span>Current:</span> <strong>$${parseFloat(request.current_weekday_cash_rate || 0).toFixed(2)}</strong>
          </div>
          <div style="font-size: 10px; margin-bottom: 2px;">
            <span>Requested:</span> <strong style="color: #16A34A;">$${parseFloat(request.requested_weekday_cash_rate || 0).toFixed(2)}</strong>
          </div>
        </div>
      `;
    }
    
    if (request.current_weekend_cash_rate > 0 || request.requested_weekend_cash_rate > 0) {
      ratesHTML += `
        <div style="background: #FAF5FF; padding: 8px; border-left: 3px solid #9333EA;">
          <div style="font-weight: bold; font-size: 10px; margin-bottom: 4px;">Weekend Cash / 周末现金</div>
          <div style="font-size: 10px; margin-bottom: 2px;">
            <span>Current:</span> <strong>$${parseFloat(request.current_weekend_cash_rate || 0).toFixed(2)}</strong>
          </div>
          <div style="font-size: 10px; margin-bottom: 2px;">
            <span>Requested:</span> <strong style="color: #9333EA;">$${parseFloat(request.requested_weekend_cash_rate || 0).toFixed(2)}</strong>
          </div>
        </div>
      `;
    }
    
    ratesHTML += '</div>';

    // Build comparison table HTML if available
    let comparisonHTML = '';
    if (comparisonData.length > 0 && employeeInfo) {
      // Calculate averages
      const employeesWithRates = comparisonData.filter(e => e.hasRate && e.check_rate > 0);
      const avgCheckRate = employeesWithRates.length > 0 
        ? employeesWithRates.reduce((sum, e) => sum + e.check_rate, 0) / employeesWithRates.length 
        : 0;
      const avgWeekdayCash = comparisonData.filter(e => e.hasRate && e.weekday_cash_rate > 0).length > 0
        ? comparisonData.filter(e => e.hasRate && e.weekday_cash_rate > 0).reduce((sum, e) => sum + e.weekday_cash_rate, 0) / comparisonData.filter(e => e.hasRate && e.weekday_cash_rate > 0).length
        : 0;
      const avgWeekendCash = comparisonData.filter(e => e.hasRate && e.weekend_cash_rate > 0).length > 0
        ? comparisonData.filter(e => e.hasRate && e.weekend_cash_rate > 0).reduce((sum, e) => sum + e.weekend_cash_rate, 0) / comparisonData.filter(e => e.hasRate && e.weekend_cash_rate > 0).length
        : 0;

      comparisonHTML = `
        <hr style="border: 1px solid #ccc; margin: 15px 0;" />
        
        <!-- Rate Comparison Table -->
        <div style="margin-bottom: 10px;">
          <h3 style="margin: 0 0 6px 0; font-size: 13px; font-weight: bold;">RATE COMPARISON / 薪资对比</h3>
          <p style="font-size: 9px; color: #666; margin-bottom: 8px;">
            ${employeeInfo.job_role} (${employeeInfo.job_level}) - ${employeeInfo.employment_type} - ${comparisonData.length} similar employees
          </p>
          <table style="width: 100%; border-collapse: collapse; font-size: 9px; margin-bottom: 8px;">
            <thead>
              <tr style="background: #E0F2FE;">
                <th style="border: 1px solid #93C5FD; padding: 6px; text-align: left; font-weight: bold;">Employee</th>
                <th style="border: 1px solid #93C5FD; padding: 6px; text-align: right; font-weight: bold;">Check Rate</th>
                <th style="border: 1px solid #93C5FD; padding: 6px; text-align: right; font-weight: bold;">Weekday Cash</th>
                <th style="border: 1px solid #93C5FD; padding: 6px; text-align: right; font-weight: bold;">Weekend Cash</th>
              </tr>
            </thead>
            <tbody>
              ${comparisonData.map((emp, idx) => `
                <tr style="background: ${idx % 2 === 0 ? '#FFFFFF' : '#F0F9FF'};">
                  <td style="border: 1px solid #93C5FD; padding: 5px;">
                    <div style="font-weight: 500;">${emp.employee_name}</div>
                    <div style="font-size: 8px; color: #666;">${emp.employee_number}</div>
                  </td>
                  <td style="border: 1px solid #93C5FD; padding: 5px; text-align: right;">
                    ${emp.hasRate && emp.check_rate > 0 ? formatCurrency(emp.check_rate) : 'N/A'}
                  </td>
                  <td style="border: 1px solid #93C5FD; padding: 5px; text-align: right;">
                    ${emp.hasRate && emp.weekday_cash_rate > 0 ? formatCurrency(emp.weekday_cash_rate) : 'N/A'}
                  </td>
                  <td style="border: 1px solid #93C5FD; padding: 5px; text-align: right;">
                    ${emp.hasRate && emp.weekend_cash_rate > 0 ? formatCurrency(emp.weekend_cash_rate) : 'N/A'}
                  </td>
                </tr>
              `).join('')}
              <tr style="background: #BFDBFE; font-weight: bold;">
                <td style="border: 1px solid #93C5FD; padding: 6px;">Average</td>
                <td style="border: 1px solid #93C5FD; padding: 6px; text-align: right;">
                  ${avgCheckRate > 0 ? formatCurrency(avgCheckRate) : 'N/A'}
                </td>
                <td style="border: 1px solid #93C5FD; padding: 6px; text-align: right;">
                  ${avgWeekdayCash > 0 ? formatCurrency(avgWeekdayCash) : 'N/A'}
                </td>
                <td style="border: 1px solid #93C5FD; padding: 6px; text-align: right;">
                  ${avgWeekendCash > 0 ? formatCurrency(avgWeekendCash) : 'N/A'}
                </td>
              </tr>
              <tr style="background: #FEF3C7; border-top: 2px solid #F59E0B;">
                <td style="border: 1px solid #93C5FD; padding: 6px; font-weight: bold;">Requested Rate</td>
                <td style="border: 1px solid #93C5FD; padding: 6px; text-align: right; font-weight: bold;">
                  ${formatCurrency(request.requested_check_rate)}
                  ${avgCheckRate > 0 && request.requested_check_rate > 0 ? `
                    <div style="font-size: 8px; color: ${request.requested_check_rate > avgCheckRate ? '#16A34A' : '#DC2626'};">
                      (${request.requested_check_rate > avgCheckRate ? '+' : ''}${((request.requested_check_rate - avgCheckRate) / avgCheckRate * 100).toFixed(1)}% vs avg)
                    </div>
                  ` : ''}
                </td>
                <td style="border: 1px solid #93C5FD; padding: 6px; text-align: right; font-weight: bold;">
                  ${formatCurrency(request.requested_weekday_cash_rate)}
                </td>
                <td style="border: 1px solid #93C5FD; padding: 6px; text-align: right; font-weight: bold;">
                  ${formatCurrency(request.requested_weekend_cash_rate)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      `;
    }
    
    // Create compact HTML content with proper Chinese font support
    const htmlContent = `
      <div id="pdf-content" style="width: 210mm; padding: 10mm; font-family: 'Microsoft YaHei', 'SimHei', 'STHeiti', Arial, sans-serif; background: white; color: black;">
        <!-- Compact Header -->
        <div style="text-align: center; margin-bottom: 12px; border-bottom: 2px solid #333; padding-bottom: 8px;">
          <h1 style="margin: 0; font-size: 22px; font-weight: bold;">PAY RATE ADJUSTMENT REQUEST</h1>
          <h2 style="margin: 2px 0 0 0; font-size: 18px; font-weight: bold;">员工加薪申请表</h2>
        </div>
        
        <!-- Compact Request Info -->
        <div style="margin-bottom: 10px; font-size: 11px; display: flex; justify-content: space-between;">
          <div><strong>Request # / 申请编号:</strong> ${request.request_no}</div>
          <div><strong>Status / 状态:</strong> ${request.status.toUpperCase()}</div>
          <div><strong>Date / 日期:</strong> ${formatDate(request.submitted_at)}</div>
        </div>
        
        <hr style="border: 1px solid #ccc; margin: 10px 0;" />
        
        <!-- Compact Employee Information -->
        <div style="margin-bottom: 10px;">
          <h3 style="margin: 0 0 6px 0; font-size: 13px; font-weight: bold;">EMPLOYEE INFORMATION / 员工信息</h3>
          <div style="font-size: 10px; line-height: 1.5; display: grid; grid-template-columns: repeat(2, 1fr); gap: 4px;">
            <div><strong>Employee Number / 员工编号:</strong> ${request.employee_number}</div>
            <div><strong>Employee Name / 姓名:</strong> ${payrollName || request.employee_name || request.employee_number}</div>
            <div><strong>Company / 公司:</strong> ${request.company}</div>
            ${request.department ? `<div><strong>Department / 部门:</strong> ${request.department}</div>` : ''}
            ${lastRateAdjustmentDate ? `<div><strong>Last Rate Adjustment / 上次调薪:</strong> ${formatDate(lastRateAdjustmentDate)}</div>` : ''}
          </div>
        </div>
        
        <hr style="border: 1px solid #ccc; margin: 10px 0;" />
        
        <!-- Compact Rate Changes -->
        <div style="margin-bottom: 10px;">
          <h3 style="margin: 0 0 6px 0; font-size: 13px; font-weight: bold;">REQUESTED RATE CHANGES / 申请薪资变更</h3>
          ${ratesHTML}
        </div>
        
        ${comparisonHTML}
        
        <hr style="border: 1px solid #ccc; margin: 10px 0;" />
        
        <!-- Compact Request Details -->
        <div style="margin-bottom: 10px;">
          <h3 style="margin: 0 0 6px 0; font-size: 13px; font-weight: bold;">REQUEST DETAILS / 申请详情</h3>
          <div style="font-size: 10px; line-height: 1.4; margin-bottom: 8px;">
            <div style="margin-bottom: 4px;"><strong>Reason / 原因:</strong> ${request.reason || 'N/A'}</div>
            <div><strong>Requested Effective Date / 申请生效日期:</strong> ${formatDate(request.requested_effective_date)}</div>
          </div>
          
          <div style="margin-top: 8px;">
            <div style="font-weight: bold; font-size: 10px; margin-bottom: 4px;">Justification / 理由说明:</div>
            <div style="background: #F9FAFB; padding: 8px; border-radius: 4px; font-size: 9px; line-height: 1.4; white-space: pre-wrap; min-height: 40px;">
${request.justification || 'N/A'}
            </div>
          </div>
          
          ${request.achievements ? `
            <div style="margin-top: 8px;">
              <div style="font-weight: bold; font-size: 10px; margin-bottom: 4px;">Achievements / 工作成就:</div>
              <div style="background: #F9FAFB; padding: 8px; border-radius: 4px; font-size: 9px; line-height: 1.4; white-space: pre-wrap; min-height: 30px;">
${request.achievements}
              </div>
            </div>
          ` : ''}
        </div>
        
        ${(request.status === 'approved' || request.status === 'rejected') ? `
          <hr style="border: 1px solid #ccc; margin: 10px 0;" />
          
          <!-- Compact Approval Section -->
          <div style="margin-bottom: 10px;">
            <h3 style="margin: 0 0 6px 0; font-size: 13px; font-weight: bold; color: ${request.status === 'approved' ? '#16A34A' : '#DC2626'};">
              ${request.status === 'approved' ? 'APPROVED / 已批准' : 'REJECTED / 已拒绝'}
            </h3>
            <div style="font-size: 10px; line-height: 1.4;">
              ${request.approved_by ? `<div style="margin-bottom: 3px;"><strong>Approved By / 批准人:</strong> ${request.approved_by}</div>` : ''}
              ${request.approved_at ? `<div style="margin-bottom: 3px;"><strong>Approval Date / 批准日期:</strong> ${formatDate(request.approved_at)}</div>` : ''}
              ${request.final_effective_date ? `<div style="margin-bottom: 3px;"><strong>Effective Date / 生效日期:</strong> ${formatDate(request.final_effective_date)}</div>` : ''}
              ${request.final_approval_notes ? `
                <div style="margin-top: 6px;">
                  <strong>Approval Notes / 批准备注:</strong>
                  <div style="background: #F0FDF4; padding: 6px; border-radius: 4px; margin-top: 3px; font-size: 9px;">${request.final_approval_notes}</div>
                </div>
              ` : ''}
              ${request.rejection_reason ? `
                <div style="margin-top: 6px;">
                  <strong>Rejection Reason / 拒绝原因:</strong>
                  <div style="background: #FEF2F2; padding: 6px; border-radius: 4px; margin-top: 3px; font-size: 9px;">${request.rejection_reason}</div>
                </div>
              ` : ''}
            </div>
          </div>
        ` : `
          <hr style="border: 1px solid #ccc; margin: 10px 0;" />
          
          <!-- Compact Signature Section -->
          <div style="margin-top: 20px;">
            <h3 style="margin: 0 0 12px 0; font-size: 13px; font-weight: bold;">APPROVAL SIGNATURES / 审批签名</h3>
            <div style="display: flex; justify-content: space-between; margin-top: 15px;">
              <div style="width: 45%;">
                <div style="font-size: 10px; font-weight: bold; margin-bottom: 15px;">Manager Approval / 经理批准:</div>
                <div style="border-bottom: 2px solid #000; margin-bottom: 6px;"></div>
                <div style="font-size: 9px;">Date / 日期: ______________</div>
              </div>
              <div style="width: 45%;">
                <div style="font-size: 10px; font-weight: bold; margin-bottom: 15px;">HR Approval / 人事批准:</div>
                <div style="border-bottom: 2px solid #000; margin-bottom: 6px;"></div>
                <div style="font-size: 9px;">Date / 日期: ______________</div>
              </div>
            </div>
          </div>
        `}
      </div>
    `;
    
    // Create temporary container
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.width = '210mm';
    container.innerHTML = htmlContent;
    document.body.appendChild(container);
    
    try {
      // Convert HTML to canvas with higher scale for better quality
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: container.scrollWidth,
        height: container.scrollHeight
      });
      
      // Create PDF from canvas with proper multi-page handling
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Calculate how many pages we need
      const totalPages = Math.ceil(imgHeight / pageHeight);
      
      // Add image across multiple pages
      for (let i = 0; i < totalPages; i++) {
        if (i > 0) {
          pdf.addPage();
        }
        
        // Calculate the y position for this page
        const yPosition = -(i * pageHeight);
        
        // Add the image, offsetting it so the correct portion shows on each page
        pdf.addImage(imgData, 'PNG', 0, yPosition, imgWidth, imgHeight);
      }
      
      // Save PDF
      pdf.save(`Rate_Request_${request.request_no}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      // Clean up
      document.body.removeChild(container);
    }
  };

  // Fetch employee info and comparison data
  useEffect(() => {
    if (isOpen && request) {
      fetchEmployeeInfoAndComparison();
      fetchLastRateAdjustmentDate();
    }
  }, [isOpen, request]);
  
  const fetchLastRateAdjustmentDate = async () => {
    try {
      // Get last_rate_adjustment_date and payroll_name from employees table
      const { data: empData, error } = await supabase
        .from('employees')
        .select('last_rate_adjustment_date, payroll_name')
        .eq('employee_number', request.employee_number)
        .single();
      
      if (error) {
        console.error('Error fetching employee data:', error);
        // Fallback: try to get from employee_pay_rates if employees table doesn't have it yet
        const { data: ratesData, error: ratesError } = await supabase
          .from('employee_pay_rates')
          .select('effective_date, updated_at, created_at')
          .eq('employee_number', request.employee_number)
          .eq('company', request.company)
          .order('created_at', { ascending: false })
          .limit(1);
        
        if (!ratesError && ratesData && ratesData.length > 0) {
          const rate = ratesData[0];
          const adjustmentDate = rate.effective_date || rate.updated_at || rate.created_at;
          setLastRateAdjustmentDate(adjustmentDate);
        } else {
          setLastRateAdjustmentDate(null);
        }
        setPayrollName(null);
        return;
      }
      
      if (empData?.last_rate_adjustment_date) {
        setLastRateAdjustmentDate(empData.last_rate_adjustment_date);
      } else {
        setLastRateAdjustmentDate(null);
      }
      
      // Set payroll_name
      if (empData?.payroll_name) {
        setPayrollName(empData.payroll_name);
      } else {
        setPayrollName(null);
      }
    } catch (error) {
      console.error('Error in fetchLastRateAdjustmentDate:', error);
      setLastRateAdjustmentDate(null);
      setPayrollName(null);
    }
  };

  const fetchEmployeeInfoAndComparison = async () => {
    try {
      setComparisonLoading(true);
      
      // First, get the employee's job_role, job_level, and employment_type
      const { data: empData, error: empError } = await supabase
        .from('employees')
        .select('job_role, job_level, employment_type')
        .eq('employee_number', request.employee_number)
        .single();

      if (empError) {
        console.error('Error fetching employee info:', empError);
        setComparisonLoading(false);
        return;
      }

      setEmployeeInfo(empData);

      // If we don't have job_role, job_level, or employment_type, we can't do comparison
      if (!empData?.job_role || !empData?.job_level || !empData?.employment_type) {
        setComparisonLoading(false);
        return;
      }

      // Find other employees with same job_role, job_level, and employment_type
      const { data: similarEmployees, error: similarError } = await supabase
        .from('employees')
        .select('employee_number, payroll_name, job_role, job_level, employment_type')
        .eq('job_role', empData.job_role)
        .eq('job_level', empData.job_level)
        .eq('employment_type', empData.employment_type)
        .neq('employee_number', request.employee_number) // Exclude the requesting employee
        .eq('status', 'Active'); // Only active employees

      if (similarError) {
        console.error('Error fetching similar employees:', similarError);
        setComparisonLoading(false);
        return;
      }

      if (!similarEmployees || similarEmployees.length === 0) {
        setComparisonLoading(false);
        return;
      }

      // Get current pay rates for these employees (for all companies, not just request company)
      const employeeNumbers = similarEmployees.map(emp => emp.employee_number);
      const { data: ratesData, error: ratesError } = await supabase
        .from('employee_pay_rates')
        .select('employee_number, company, check_rate, weekday_cash_rate, weekend_cash_rate')
        .in('employee_number', employeeNumbers)
        .is('end_date', null); // Only active rates - removed company filter to get all rates

      if (ratesError) {
        console.error('Error fetching rates:', ratesError);
        setComparisonLoading(false);
        return;
      }

      // Combine employee info with their rates
      const comparison = similarEmployees.map(emp => {
        const empRates = ratesData?.filter(r => r.employee_number === emp.employee_number) || [];
        // Prefer rate matching request company, otherwise use any available rate
        const rate = empRates.find(r => r.company === request.company) || empRates[0];
        
        return {
          employee_number: emp.employee_number,
          employee_name: emp.payroll_name || emp.employee_number,
          check_rate: rate ? parseFloat(rate.check_rate || 0) : 0,
          weekday_cash_rate: rate ? parseFloat(rate.weekday_cash_rate || 0) : 0,
          weekend_cash_rate: rate ? parseFloat(rate.weekend_cash_rate || 0) : 0,
          hasRate: rate !== undefined, // Track if employee has any rate record
        };
      }); // Include all employees, even if they don't have rates

      // Sort by check_rate descending
      comparison.sort((a, b) => (b.check_rate || 0) - (a.check_rate || 0));

      setComparisonData(comparison);
    } catch (error) {
      console.error('Error in fetchEmployeeInfoAndComparison:', error);
    } finally {
      setComparisonLoading(false);
    }
  };

  const calculateIncrease = (current, approved) => {
    const currentVal = parseFloat(current) || 0;
    const approvedVal = parseFloat(approved) || 0;
    const increase = approvedVal - currentVal;
    const percentage = currentVal > 0 ? ((increase / currentVal) * 100) : 0;
    
    return {
      amount: increase,
      percentage: percentage,
      formatted: increase >= 0 ? `+$${increase.toFixed(2)}` : `-$${Math.abs(increase).toFixed(2)}`,
      percentFormatted: `${percentage >= 0 ? '+' : ''}${percentage.toFixed(1)}%`
    };
  };

  const handleApprove = async () => {
    if (!effectiveDate) {
      alert('Please set an effective date');
      return;
    }

    try {
      setIsSubmitting(true);

      const updateData = {
        status: 'approved',
        approved_check_rate: parseFloat(approvedRates.check_rate),
        approved_weekday_cash_rate: parseFloat(approvedRates.weekday_cash_rate),
        approved_weekend_cash_rate: parseFloat(approvedRates.weekend_cash_rate),
        approved_effective_date: effectiveDate,
        final_approver: 'admin', // You can customize this based on logged-in admin
        final_approved_at: new Date().toISOString(),
        final_approval_notes: approvalNotes || null,
        updated_at: new Date().toISOString()
      };

      const { error: updateError } = await supabase
        .from('pay_rate_adjustment_requests')
        .update(updateData)
        .eq('id', request.id);

      if (updateError) {
        console.error('Error approving request:', updateError);
        alert('Error approving request: ' + updateError.message);
        return;
      }

      // NOTE: Rate changes are applied MANUALLY by admin
      // This approval just marks the request as approved for tracking purposes
      
      alert(`✅ Request ${request.request_no} has been approved!\n\n⚠️ IMPORTANT: You must manually update the employee's pay rates in your payroll system.\n\nApproved Rates:\n• Check Rate: $${parseFloat(approvedRates.check_rate).toFixed(2)}/hr\n• Weekday Cash: $${parseFloat(approvedRates.weekday_cash_rate).toFixed(2)}/hr\n• Weekend Cash: $${parseFloat(approvedRates.weekend_cash_rate).toFixed(2)}/hr\n\nEffective Date: ${formatDate(effectiveDate)}`);
      
      if (onApprovalComplete) {
        onApprovalComplete();
      }
    } catch (error) {
      console.error('Error in handleApprove:', error);
      alert('Error approving request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason || rejectionReason.trim().length < 10) {
      alert('Please provide a rejection reason (minimum 10 characters)');
      return;
    }

    try {
      setIsSubmitting(true);

      const updateData = {
        status: 'rejected',
        rejected_by: 'admin',
        rejected_at: new Date().toISOString(),
        rejection_reason: rejectionReason.trim(),
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('pay_rate_adjustment_requests')
        .update(updateData)
        .eq('id', request.id);

      if (error) {
        console.error('Error rejecting request:', error);
        alert('Error rejecting request: ' + error.message);
        return;
      }

      alert(`❌ Request ${request.request_no} has been rejected.`);
      
      if (onApprovalComplete) {
        onApprovalComplete();
      }
    } catch (error) {
      console.error('Error in handleReject:', error);
      alert('Error rejecting request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = () => {
    if (action === 'approve') {
      handleApprove();
    } else if (action === 'reject') {
      handleReject();
    }
  };

  if (!isOpen) return null;

  const checkIncrease = calculateIncrease(request.current_check_rate, approvedRates.check_rate);
  const weekdayIncrease = calculateIncrease(request.current_weekday_cash_rate, approvedRates.weekday_cash_rate);
  const weekendIncrease = calculateIncrease(request.current_weekend_cash_rate, approvedRates.weekend_cash_rate);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full my-4">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-t-lg relative">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold">Review Rate Adjustment Request</h2>
              <p className="text-blue-100 text-xs mt-0.5">Request #{request.request_no}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={downloadRequestPDF}
                disabled={isSubmitting}
                className="text-white hover:text-gray-200 transition-colors p-2 rounded hover:bg-blue-800 flex-shrink-0 flex items-center gap-1.5"
                title="Download PDF"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="text-sm font-medium">PDF</span>
              </button>
              <button
                onClick={onClose}
                disabled={isSubmitting}
                className="text-white hover:text-gray-200 transition-colors p-1 rounded hover:bg-blue-800 flex-shrink-0"
                title="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-3 max-h-[calc(100vh-200px)] overflow-y-auto">
          {!action ? (
            /* Initial Review Screen */
            <>
              {/* Employee Info */}
              <div className="bg-gray-50 rounded-lg p-3">
                <h3 className="font-semibold text-gray-700 mb-2 text-sm">Employee Information</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                  <div>
                    <p className="text-gray-500">Name</p>
                    <p className="font-semibold">{payrollName || request.employee_name || request.employee_number}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Employee #</p>
                    <p className="font-semibold">{request.employee_number}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Company</p>
                    <p className="font-semibold">{request.company}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Department</p>
                    <p className="font-semibold">{request.department || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Last Rate Adjustment</p>
                    <p className="font-semibold">
                      {lastRateAdjustmentDate ? formatDate(lastRateAdjustmentDate) : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Comparison Table - Show rates of similar employees */}
              {comparisonLoading ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-700">Loading comparison data...</p>
                </div>
              ) : comparisonData.length > 0 && employeeInfo ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 mb-3">
                  <h3 className="font-semibold text-blue-900 mb-1 text-sm">
                    📊 Rate Comparison: {employeeInfo.job_role} ({employeeInfo.job_level}) - {employeeInfo.employment_type}
                  </h3>
                  <p className="text-xs text-blue-700 mb-2">
                    {comparisonData.length} employee{comparisonData.length !== 1 ? 's' : ''} with same role/level/type
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-blue-100">
                          <th className="px-2 py-1.5 text-left font-semibold text-blue-900">Employee</th>
                          <th className="px-2 py-1.5 text-right font-semibold text-blue-900">Check Rate</th>
                          <th className="px-2 py-1.5 text-right font-semibold text-blue-900">Weekday Cash</th>
                          <th className="px-2 py-1.5 text-right font-semibold text-blue-900">Weekend Cash</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comparisonData.map((emp, idx) => (
                          <tr key={emp.employee_number} className={idx % 2 === 0 ? 'bg-white' : 'bg-blue-50'}>
                            <td className="px-2 py-1.5">
                              <div className="font-medium text-gray-900 text-xs">{emp.employee_name}</div>
                              <div className="text-xs text-gray-500">{emp.employee_number}</div>
                            </td>
                            <td className="px-2 py-1.5 text-right text-xs">
                              {emp.hasRate ? formatCurrency(emp.check_rate) : 'N/A'}
                            </td>
                            <td className="px-2 py-1.5 text-right text-xs">
                              {emp.hasRate ? formatCurrency(emp.weekday_cash_rate) : 'N/A'}
                            </td>
                            <td className="px-2 py-1.5 text-right text-xs">
                              {emp.hasRate ? formatCurrency(emp.weekend_cash_rate) : 'N/A'}
                            </td>
                          </tr>
                        ))}
                        {/* Show statistics */}
                        <tr className="bg-blue-200 font-semibold">
                          <td className="px-2 py-1.5 text-blue-900 text-xs">Average</td>
                          <td className="px-2 py-1.5 text-right text-blue-900 text-xs">
                            {(() => {
                              const employeesWithRates = comparisonData.filter(e => e.hasRate && e.check_rate > 0);
                              if (employeesWithRates.length === 0) return 'N/A';
                              return formatCurrency(
                                employeesWithRates.reduce((sum, e) => sum + e.check_rate, 0) / employeesWithRates.length
                              );
                            })()}
                          </td>
                          <td className="px-2 py-1.5 text-right text-blue-900 text-xs">
                            {(() => {
                              const employeesWithRates = comparisonData.filter(e => e.hasRate && e.weekday_cash_rate > 0);
                              if (employeesWithRates.length === 0) return 'N/A';
                              return formatCurrency(
                                employeesWithRates.reduce((sum, e) => sum + e.weekday_cash_rate, 0) / employeesWithRates.length
                              );
                            })()}
                          </td>
                          <td className="px-2 py-1.5 text-right text-blue-900 text-xs">
                            {(() => {
                              const employeesWithRates = comparisonData.filter(e => e.hasRate && e.weekend_cash_rate > 0);
                              if (employeesWithRates.length === 0) return 'N/A';
                              return formatCurrency(
                                employeesWithRates.reduce((sum, e) => sum + e.weekend_cash_rate, 0) / employeesWithRates.length
                              );
                            })()}
                          </td>
                        </tr>
                        {/* Show requested rates for comparison */}
                        <tr className="bg-yellow-100 border-t-2 border-yellow-300">
                          <td className="px-2 py-1.5 font-semibold text-yellow-900 text-xs">Requested Rate</td>
                          <td className="px-2 py-1.5 text-right font-bold text-yellow-900 text-xs">
                            {formatCurrency(request.requested_check_rate)}
                            {(() => {
                              const employeesWithRates = comparisonData.filter(e => e.hasRate && e.check_rate > 0);
                              if (employeesWithRates.length === 0) return null;
                              const avgCheckRate = employeesWithRates.reduce((sum, e) => sum + e.check_rate, 0) / employeesWithRates.length;
                              if (avgCheckRate > 0 && request.requested_check_rate > 0) {
                                const diffPct = ((request.requested_check_rate - avgCheckRate) / avgCheckRate * 100).toFixed(1);
                                return (
                                  <span className={`text-xs ml-1 ${diffPct > 0 ? 'text-green-700' : 'text-red-700'}`}>
                                    ({diffPct > 0 ? '+' : ''}{diffPct}% vs avg)
                                  </span>
                                );
                              }
                              return null;
                            })()}
                          </td>
                          <td className="px-2 py-1.5 text-right font-bold text-yellow-900 text-xs">
                            {formatCurrency(request.requested_weekday_cash_rate)}
                          </td>
                          <td className="px-2 py-1.5 text-right font-bold text-yellow-900 text-xs">
                            {formatCurrency(request.requested_weekend_cash_rate)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : employeeInfo && (!employeeInfo.job_role || !employeeInfo.job_level || !employeeInfo.employment_type) ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-yellow-700">
                    ⚠️ Cannot show comparison: Employee's job role, level, or employment type is not set in their profile.
                  </p>
                </div>
              ) : null}

              {/* Rate Comparison Table */}
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <h3 className="font-semibold text-gray-700 p-2.5 border-b bg-gray-50 text-sm">Requested Rate Changes</h3>
                <table className="w-full text-xs">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-2 py-2 text-left font-semibold">Rate Type</th>
                      <th className="px-2 py-2 text-right font-semibold">Current</th>
                      <th className="px-2 py-2 text-center font-semibold">→</th>
                      <th className="px-2 py-2 text-right font-semibold">Requested</th>
                      <th className="px-2 py-2 text-right font-semibold">Increase</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t">
                      <td className="px-2 py-2 font-semibold">Check Rate</td>
                      <td className="px-2 py-2 text-right">{formatCurrency(request.current_check_rate)}/hr</td>
                      <td className="px-2 py-2 text-center text-gray-400">→</td>
                      <td className="px-2 py-2 text-right font-bold text-blue-700">{formatCurrency(request.requested_check_rate)}/hr</td>
                      <td className="px-2 py-2 text-right">
                        <span className="text-green-600 font-semibold">
                          {formatCurrency(request.check_rate_increase)} ({request.check_rate_increase_pct?.toFixed(1)}%)
                        </span>
                      </td>
                    </tr>
                    <tr className="border-t">
                      <td className="px-2 py-2 font-semibold">Weekday Cash</td>
                      <td className="px-2 py-2 text-right">{formatCurrency(request.current_weekday_cash_rate)}/hr</td>
                      <td className="px-2 py-2 text-center text-gray-400">→</td>
                      <td className="px-2 py-2 text-right font-bold text-green-700">{formatCurrency(request.requested_weekday_cash_rate)}/hr</td>
                      <td className="px-2 py-2 text-right">
                        <span className="text-green-600 font-semibold">
                          {formatCurrency(request.weekday_cash_rate_increase)} ({request.weekday_cash_increase_pct?.toFixed(1)}%)
                        </span>
                      </td>
                    </tr>
                    <tr className="border-t">
                      <td className="px-2 py-2 font-semibold">Weekend Cash</td>
                      <td className="px-2 py-2 text-right">{formatCurrency(request.current_weekend_cash_rate)}/hr</td>
                      <td className="px-2 py-2 text-center text-gray-400">→</td>
                      <td className="px-2 py-2 text-right font-bold text-purple-700">{formatCurrency(request.requested_weekend_cash_rate)}/hr</td>
                      <td className="px-2 py-2 text-right">
                        <span className="text-green-600 font-semibold">
                          {formatCurrency(request.weekend_cash_rate_increase)} ({request.weekend_cash_increase_pct?.toFixed(1)}%)
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Request Details */}
              <div className="space-y-2">
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-1">Reason:</p>
                  <p className="text-xs text-gray-600 bg-gray-50 p-2 rounded">{request.reason}</p>
                </div>

                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-1">Justification:</p>
                  <p className="text-xs text-gray-600 bg-gray-50 p-2 rounded whitespace-pre-wrap max-h-24 overflow-y-auto">{request.justification}</p>
                </div>

                {request.achievements && (
                  <div>
                    <p className="text-xs font-semibold text-gray-700 mb-1">Achievements:</p>
                    <p className="text-xs text-gray-600 bg-gray-50 p-2 rounded whitespace-pre-wrap max-h-24 overflow-y-auto">{request.achievements}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs font-semibold text-gray-700 mb-1">Requested Effective Date:</p>
                    <p className="text-xs text-gray-600">{formatDate(request.requested_effective_date)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-700 mb-1">Submitted:</p>
                    <p className="text-xs text-gray-600">{formatDate(request.submitted_at)}</p>
                  </div>
                </div>
              </div>

              {/* Decision Buttons */}
              <div className="flex gap-4 pt-4 border-t">
                <button
                  onClick={() => setAction('approve')}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Approve Request
                </button>
                <button
                  onClick={() => setAction('reject')}
                  className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Reject Request
                </button>
              </div>
            </>
          ) : action === 'approve' ? (
            /* Approval Form */
            <>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="font-semibold text-green-800">✅ Approving Request {request.request_no}</p>
                <p className="text-sm text-gray-700 mt-1">Set the approved rates and effective date below.</p>
              </div>

              {/* Custom Rates Toggle */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="useCustomRates"
                  checked={useCustomRates}
                  onChange={(e) => {
                    setUseCustomRates(e.target.checked);
                    if (!e.target.checked) {
                      // Reset to requested rates
                      setApprovedRates({
                        check_rate: request.requested_check_rate,
                        weekday_cash_rate: request.requested_weekday_cash_rate,
                        weekend_cash_rate: request.requested_weekend_cash_rate
                      });
                    }
                  }}
                  className="w-4 h-4"
                />
                <label htmlFor="useCustomRates" className="text-sm font-semibold text-gray-700">
                  Approve with modified rates (different from requested)
                </label>
              </div>

              {/* Approved Rates */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-700 mb-3">Approved Rates</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 uppercase font-semibold mb-1">Check Rate ($/hr)</label>
                    <input
                      type="number"
                      value={approvedRates.check_rate}
                      onChange={(e) => setApprovedRates({ ...approvedRates, check_rate: e.target.value })}
                      disabled={!useCustomRates}
                      step="0.01"
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
                    />
                    {useCustomRates && (
                      <p className="text-xs text-green-600 font-semibold mt-1">
                        {checkIncrease.formatted} ({checkIncrease.percentFormatted})
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 uppercase font-semibold mb-1">Weekday Cash ($/hr)</label>
                    <input
                      type="number"
                      value={approvedRates.weekday_cash_rate}
                      onChange={(e) => setApprovedRates({ ...approvedRates, weekday_cash_rate: e.target.value })}
                      disabled={!useCustomRates}
                      step="0.01"
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
                    />
                    {useCustomRates && (
                      <p className="text-xs text-green-600 font-semibold mt-1">
                        {weekdayIncrease.formatted} ({weekdayIncrease.percentFormatted})
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 uppercase font-semibold mb-1">Weekend Cash ($/hr)</label>
                    <input
                      type="number"
                      value={approvedRates.weekend_cash_rate}
                      onChange={(e) => setApprovedRates({ ...approvedRates, weekend_cash_rate: e.target.value })}
                      disabled={!useCustomRates}
                      step="0.01"
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
                    />
                    {useCustomRates && (
                      <p className="text-xs text-green-600 font-semibold mt-1">
                        {weekendIncrease.formatted} ({weekendIncrease.percentFormatted})
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Effective Date */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Approved Effective Date *</label>
                <input
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  required
                />
              </div>

              {/* Approval Notes */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Approval Notes <span className="text-xs text-gray-500 font-normal">(optional, visible to employee)</span>
                </label>
                <textarea
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  rows="3"
                  placeholder="Add any notes or comments about this approval..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <button
                  onClick={() => setAction(null)}
                  disabled={isSubmitting}
                  className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition-colors disabled:opacity-50"
                >
                  ← Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !effectiveDate}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Approving...
                    </>
                  ) : (
                    '✅ Confirm Approval'
                  )}
                </button>
              </div>
            </>
          ) : (
            /* Rejection Form */
            <>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="font-semibold text-red-800">❌ Rejecting Request {request.request_no}</p>
                <p className="text-sm text-gray-700 mt-1">Please provide a clear reason for rejection.</p>
              </div>

              {/* Rejection Reason */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Rejection Reason * <span className="text-xs text-gray-500 font-normal">(minimum 10 characters, visible to employee)</span>
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows="4"
                  placeholder="Explain why this request is being rejected..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">{rejectionReason.length} characters</p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <button
                  onClick={() => setAction(null)}
                  disabled={isSubmitting}
                  className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition-colors disabled:opacity-50"
                >
                  ← Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || rejectionReason.trim().length < 10}
                  className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Rejecting...
                    </>
                  ) : (
                    '❌ Confirm Rejection'
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RateApprovalModal;

